import type { Plugin_Listing } from '@oneappexchange/oaf-schema';
import type { ListingResponse } from '../services/listing_service';
import { AI_PLUGIN_EXTENSION_TYPE } from './aiListingExtensionTypes';
import { parseRepoFolderUrl, resolveSourceRef } from './skill_utils';
import type { SkillSource } from './skill_utils';

/**
 * Helpers for reading plugin-specific data off a listing.
 *
 * A plugin listing carries an ai-plugin extension whose `data` block holds the plugin's structured
 * fields: `skills` (the bare external_ids of its member skill listings), `domains`, `version`, and
 * `pluginDependencies`. It also carries its baked-in `readme` plus bundle-folder `repoFolderUrl`,
 * `files`, and `commitSha`. The shared Overview and Package Files components consume those fields
 * without fetching README.md from GitHub.
 */

/**
 * Forward-compatible copy of the embedded Skill card data supplied by publishing-service.
 * Keep this local intersection until the matching @oneappexchange/oaf-schema package is published.
 */
export interface PluginSkillSummary {
    externalId: string;
    name: string;
    description: string;
    logoUrl?: string;
}

type ConfigurablePluginListing = Plugin_Listing & {
    disableSkillLinks?: unknown;
    hideSpecs?: unknown;
    skillSummaries?: unknown;
};

export interface ClaudePluginInstallCommands {
    /** Non-interactive shell command shown on the CLI Script tab. */
    cli: string;
    /** Native slash command copied from the AI Prompt tab into Claude Code. */
    inClaudeCode: string;
}

// Keep the legacy native-flow association explicit and listing-specific: deriving a marketplace
// identifier from repoFolderUrl or the display name would silently generate incorrect commands.
// Listings with the authored External provision flow bypass this command in pluginSidebar and use
// their installUrl instead; this map remains only for backward-compatible native installation.
const CLAUDE_PLUGIN_INSTALL_SPECIFIERS: Readonly<Record<string, string>> = {
    'plugin-salesforce-development': 'salesforce-development@claude-plugins-official',
};
const CLAUDE_OFFICIAL_MARKETPLACE_SOURCE = 'anthropics/claude-plugins-official';

/**
 * Returns the real Claude marketplace install commands for a supported plugin listing.
 *
 * `external_id` is the canonical OAF identifier; `oafId` is the client transform's alias for the
 * same value. Unknown listings fail closed instead of receiving Salesforce Development's command.
 */
export function getClaudePluginInstallCommands(listing?: ListingResponse | null): ClaudePluginInstallCommands | null {
    const listingId = listing?.external_id || listing?.oafId || '';
    const specifier = CLAUDE_PLUGIN_INSTALL_SPECIFIERS[listingId];
    if (!specifier) {
        return null;
    }
    return {
        // `marketplace add` is idempotent: it succeeds when the marketplace is already configured
        // and bootstraps fresh Claude installations before the non-interactive plugin install.
        cli: `claude plugin marketplace add ${CLAUDE_OFFICIAL_MARKETPLACE_SOURCE} && claude plugin install ${specifier} --yes`,
        inClaudeCode: `/plugin install ${specifier}`,
    };
}

/**
 * Reads the plugin extension's `data` block off a listing, or null when the listing carries no
 * plugin extension. The single reader every plugin getter below funnels through.
 */
function getPluginExtensionData(listing?: ListingResponse | null): ConfigurablePluginListing | null {
    const extension = listing?.extensions?.find((candidate) => candidate.extensionType === AI_PLUGIN_EXTENSION_TYPE);
    return (extension?.data as ConfigurablePluginListing | undefined) ?? null;
}

/**
 * The plugin's member skill external_ids (e.g. ['skill-agentforce-generate', ...]), from the
 * structured `skills` field. Each id resolves to a full skill listing via the same
 * `/experience/listings/<id>` endpoint. Returns [] when absent.
 */
export function getPluginSkillIds(listing?: ListingResponse | null): string[] {
    const ids = getPluginExtensionData(listing)?.skills;
    return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string' && id.length > 0) : [];
}

/**
 * Whether Skills in a Plugin's Specs tab should omit navigation to standalone Skill listings.
 * The standard Plugin model remains linked unless OAF explicitly sends true.
 */
export function shouldDisablePluginSkillLinks(listing?: ListingResponse | null): boolean {
    return getPluginExtensionData(listing)?.disableSkillLinks === true;
}

/**
 * Whether a Plugin's Specs tab should be omitted. The standard Plugin model keeps Specs unless
 * publishing-service explicitly sends the boolean value true.
 */
export function shouldHidePluginSpecs(listing?: ListingResponse | null): boolean {
    return getPluginExtensionData(listing)?.hideSpecs === true;
}

/**
 * Embedded card summaries for Plugins whose Skills do not have standalone listing pages.
 * Malformed entries are ignored and duplicate external IDs are collapsed to keep LWC keys stable.
 */
export function getPluginSkillSummaries(listing?: ListingResponse | null): PluginSkillSummary[] {
    const summaries = getPluginExtensionData(listing)?.skillSummaries;
    if (!Array.isArray(summaries)) {
        return [];
    }

    const seenExternalIds = new Set<string>();
    return summaries.filter((summary: unknown): summary is PluginSkillSummary => {
        if (typeof summary !== 'object' || summary === null) {
            return false;
        }
        const candidate = summary as Record<string, unknown>;
        if (
            typeof candidate.externalId !== 'string' ||
            candidate.externalId.length === 0 ||
            typeof candidate.name !== 'string' ||
            candidate.name.length === 0 ||
            typeof candidate.description !== 'string' ||
            candidate.description.length === 0 ||
            (candidate.logoUrl !== undefined && typeof candidate.logoUrl !== 'string') ||
            seenExternalIds.has(candidate.externalId)
        ) {
            return false;
        }
        seenExternalIds.add(candidate.externalId);
        return true;
    });
}

/**
 * The plugin's version (e.g. '1.0.0'), from the structured `version` field on the plugin extension.
 *
 * Returns '' when absent, which callers treat as "not available".
 */
export function getPluginVersion(listing?: ListingResponse | null): string {
    return getPluginExtensionData(listing)?.version ?? '';
}

/**
 * The date the plugin was first listed (YYYY-MM-DD), from the structured `publishedDate` field.
 * Mirrors `getSkillPublishedDate`; '' when absent.
 *
 * The row stays hidden until ingest provides the field.
 */
export function getPluginPublishedDate(listing?: ListingResponse | null): string {
    return getPluginExtensionData(listing)?.publishedDate ?? '';
}

/**
 * The date the plugin's bundle folder was most recently changed (YYYY-MM-DD), from the structured
 * `lastModifiedDate` field. Mirrors `getSkillLastModifiedDate`; '' when absent.
 */
export function getPluginLastModifiedDate(listing?: ListingResponse | null): string {
    return getPluginExtensionData(listing)?.lastModifiedDate ?? '';
}

/**
 * The plugin's SkillDomain values (e.g. ['agentforce']), from the structured `domains` field.
 * Returns [] when absent.
 */
export function getPluginDomains(listing?: ListingResponse | null): string[] {
    const domains: unknown = getPluginExtensionData(listing)?.domains;
    return Array.isArray(domains)
        ? domains.filter((domain): domain is string => typeof domain === 'string' && domain.length > 0)
        : [];
}

/**
 * The plugin's baked-in README.md source. This is the canonical initial document for both the
 * Overview and Package Files tabs, avoiding a GitHub request just to render either landing view.
 * Returns '' when absent so Package Files can fall back to its normal lazy file fetch.
 */
export function getPluginReadme(listing?: ListingResponse | null): string {
    return getPluginExtensionData(listing)?.readme ?? '';
}

/**
 * The plugin's declared dependencies (e.g. ['platform']), from the structured `pluginDependencies` field.
 * Returns [] when absent.
 */
export function getPluginDependencies(listing?: ListingResponse | null): string[] {
    const dependencies: unknown = getPluginExtensionData(listing)?.pluginDependencies;
    return Array.isArray(dependencies)
        ? dependencies.filter(
              (dependency): dependency is string => typeof dependency === 'string' && dependency.length > 0,
          )
        : [];
}

/**
 * The plugin's opted-in file list — folder-relative paths within its bundle folder (e.g.
 * '.claude-plugin/plugin.json', 'skills/foo/bar.yaml'). Returns [] when absent.
 *
 * These are the leaves of the plugin's file tree, consumed by the shared Package Files viewer
 * (`buildSkillFileTree` synthesizes the intermediate directory nodes from them).
 */
export function getPluginFiles(listing?: ListingResponse | null): string[] {
    const files = getPluginExtensionData(listing)?.files;
    return Array.isArray(files)
        ? files.filter((file): file is string => typeof file === 'string' && file.length > 0)
        : [];
}

/**
 * Reads a plugin listing's source-repo coordinates from its plugin extension's `repoFolderUrl` (+
 * `commitSha`), or null when the listing carries no plugin extension, an unparseable URL, or no
 * usable ref (see `resolveSourceRef` in `skill_utils.ts`). Feeds the shared Package Files viewer the
 * same way `getSkillSource` does for a skill listing.
 */
export function getPluginSource(listing?: ListingResponse | null): SkillSource | null {
    const data = getPluginExtensionData(listing);
    const parsed = parseRepoFolderUrl(data?.repoFolderUrl);
    if (!parsed) {
        return null;
    }
    const ref = resolveSourceRef(data?.commitSha, parsed.ref);
    if (!ref) {
        return null;
    }
    return {
        host: parsed.host,
        owner: parsed.owner,
        repo: parsed.repo,
        path: parsed.path,
        ref,
        url: data!.repoFolderUrl!,
    };
}
